import React, { useCallback, useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import { Tooltip } from "antd";
import { Form, Formik } from "formik";
import { LuPencil } from "react-icons/lu";
import { RiDeleteBinLine } from "react-icons/ri";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import CustomModal from "../../components/CustomModal";
import FileUpload from "../../components/FileUpload";
import Input from "../../components/input";
import NewTableComponent from "../../components/NewTableComponent";
import Select from "../../components/Select";
import ConfirmDeleteModal from "../../components/modals/ConfirmDeleteModal";
import { apiService } from "../../services/apiService";
import { plusIcon } from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";
import { contentValidationSchema } from "../../utils/Schema";
import "../../styles/Access.css";
import "../../styles/Services.css";
import "../../styles/Table.css";
import "../../styles/TripCostEstimation.css";
import "../../styles/Users.css";

const CATEGORY_OPTIONS = [
  { value: "driver-training", label: "Driver Training" },
  { value: "fms-training", label: "FMS Training" },
  { value: "fms-document", label: "FMS Documentation" },
];

const CATEGORY_LABEL_MAP = {
  "driver-training": "Driver Training",
  "fms-training": "FMS Training",
  "fms-document": "FMS Documentation",
};

const TYPE_OPTIONS = [
  { value: "video", label: "Video" },
  { value: "pdf", label: "PDF" },
];

const isPdfFile = (fileName) => /\.pdf$/i.test(String(fileName || "").trim());
const isVideoFile = (fileName) =>
  /\.(mp4|mov|avi|mkv|webm|m4v|3gp|wmv|flv|ogv)$/i.test(
    String(fileName || "").trim()
  );

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
  } catch {
    return dateStr;
  }
};

const getFullFileUrl = (path) => {
  if (!path) return "";
  if (/^(https?:)?\/\//i.test(path)) return path;
  const baseUrl = (process.env.REACT_APP_API_FILES_URL || "").replace(/\/+$/, "");
  const cleanPath = String(path).replace(/^\/+/, "");
  return baseUrl ? `${baseUrl}/${cleanPath}` : cleanPath;
};

const resolveUploadedPath = (fileEntry) => {
  if (!fileEntry) return "";
  if (typeof fileEntry === "string") {
    const normalizedBase = String(process.env.REACT_APP_API_FILES_URL || "").trim().replace(/\/+$/, "");
    if (normalizedBase && fileEntry.startsWith(normalizedBase)) {
      return fileEntry.slice(normalizedBase.length).replace(/^\/+/, "");
    }
    return fileEntry.replace(/^\/+/, "");
  }
  return (
    fileEntry?.fileName ||
    fileEntry?.file_path ||
    fileEntry?.path ||
    fileEntry?.url ||
    fileEntry?.file_url ||
    fileEntry?.data?.fileName ||
    fileEntry?.data?.file_path ||
    fileEntry?.data?.path ||
    ""
  );
};

const TrainingContentList = ({ category, title, breadcrumbs }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");

  // Form Modal State
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [initialFormValues, setInitialFormValues] = useState({
    title: "",
    category: category || "driver-training",
    type: "video",
    fileUrl: "",
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Video Preview Modal State
  const [previewVideo, setPreviewVideo] = useState({
    show: false,
    url: "",
    title: "",
  });

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        search_text: searchText ? searchText.trim() : "",
        categoryFilter: {
          filter_type: "equals",
          value: category,
        },
      };

      const params = {
        page: currentPage,
        limit,
      };

      const response = await apiService.getContentPaginated(payload, params);
      const resData = response?.data || response || {};
      const results = Array.isArray(resData?.results)
        ? resData.results
        : Array.isArray(resData?.data)
        ? resData.data
        : Array.isArray(resData)
        ? resData
        : [];

      const total = Number(
        resData?.meta?.totalItems ??
        resData?.total ??
        resData?.count ??
        results.length
      );
      const pages = Number(
        resData?.meta?.totalPages ??
        Math.max(1, Math.ceil(total / Math.max(limit, 1)))
      );

      setRows(results);
      setTotalItems(total);
      setTotalPages(pages);
    } catch (err) {
      console.error("Error fetching content:", err);
      setRows([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [category, currentPage, limit, searchText]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleSearch = (val) => {
    setSearchText(val);
    setCurrentPage(1);
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setInitialFormValues({
      title: "",
      category: category || "driver-training",
      type: "video",
      fileUrl: "",
    });
    setUploadedFiles([]);
    setShowFormModal(true);
  };

  const handleOpenEdit = async (row) => {
    setEditingItem(row);
    const existingFile = row?.fileUrl || row?.file_url || row?.url || "";
    const resolvedType = row?.type === "pdf" ? "pdf" : "video";
    setInitialFormValues({
      title: row?.title || row?.name || "",
      category: category || row?.category || "driver-training",
      type: resolvedType,
      fileUrl: existingFile,
    });
    if (existingFile) {
      setUploadedFiles([
        {
          fileName: existingFile,
          originalName: existingFile.split("/").pop(),
          previewUrl: getFullFileUrl(existingFile),
        },
      ]);
    } else {
      setUploadedFiles([]);
    }
    setShowFormModal(true);

    if (row?.id) {
      try {
        const res = await apiService.getContentDetail(row.id);
        const detail = res?.data || res;
        if (detail && typeof detail === "object") {
          const detailFile = detail?.fileUrl || detail?.file_url || existingFile;
          const detailType = detail?.type === "pdf" ? "pdf" : resolvedType;
          setInitialFormValues({
            title: detail?.title || detail?.name || row?.title || "",
            category: category || detail?.category || row?.category || "driver-training",
            type: detailType,
            fileUrl: detailFile,
          });
          if (detailFile) {
            setUploadedFiles([
              {
                fileName: detailFile,
                originalName: detailFile.split("/").pop(),
                previewUrl: getFullFileUrl(detailFile),
              },
            ]);
          }
        }
      } catch (err) {
        console.warn("Detail fetch failed, using row data:", err);
      }
    }
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setEditingItem(null);
    setInitialFormValues({
      title: "",
      category: category || "driver-training",
      type: "video",
      fileUrl: "",
    });
    setUploadedFiles([]);
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    const resolvedFile =
      uploadedFiles.length > 0
        ? resolveUploadedPath(uploadedFiles[0])
        : values.fileUrl;

    if (!resolvedFile) {
      setSubmitting(false);
      return;
    }

    try {
      if (editingItem?.id) {
        const payload = {
          id: editingItem.id,
          title: values.title.trim(),
          category: values.category,
          type: values.type,
          fileUrl: resolvedFile,
        };
        await apiService.updateContent(payload);
        successNotify("Content updated successfully");
      } else {
        const payload = {
          title: values.title.trim(),
          category: values.category,
          type: values.type,
          fileUrl: resolvedFile,
        };
        await apiService.createContent(payload);
        successNotify("Content created successfully");
      }

      handleCloseFormModal();
      fetchContent();
    } catch (err) {
      errorNotify(err?.message || err || "Failed to save content");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (row) => {
    setDeletingItem(row);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem?.id) return;

    setDeleting(true);
    try {
      await apiService.deleteContent(deletingItem.id);
      successNotify("Content deleted successfully");
      setShowDeleteModal(false);
      setDeletingItem(null);
      fetchContent();
    } catch (err) {
      errorNotify(err?.message || err || "Failed to delete content");
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => (currentPage - 1) * limit + index + 1,
      minWidth: "60px",
      maxWidth: "80px",
      grow: 0,
    },
    {
      name: "Title",
      key: "title",
      selector: (row) => {
        const titleText = row?.title || row?.name || "-";
        return (
          <Tooltip title={titleText} placement="topLeft">
            <span className="truncate_cell_text">{titleText}</span>
          </Tooltip>
        );
      },
      minWidth: "220px",
      grow: 2,
    },
    {
      name: "Category",
      key: "category",
      selector: (row) => (
        <Badge bg="primary" className="text-capitalize" style={{ fontWeight: 500 }}>
          {CATEGORY_LABEL_MAP[row?.category] || row?.category || category}
        </Badge>
      ),
      minWidth: "160px",
      grow: 1,
    },
    {
      name: "Type",
      key: "type",
      selector: (row) => (
        <Badge bg="secondary" className="text-uppercase" style={{ fontWeight: 500, fontSize: "11px" }}>
          {row?.type || "video"}
        </Badge>
      ),
      minWidth: "120px",
      grow: 1,
    },
    {
      name: "File",
      key: "file",
      selector: (row) => {
        const rawUrl = row?.fileUrl || row?.file_url || row?.url;
        if (!rawUrl) return "-";
        const fullUrl = getFullFileUrl(rawUrl);
        const isPdf = isPdfFile(rawUrl) || row?.type === "pdf";
        if (isPdf) {
          return (
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="finance_link_text truncate_cell_text"
            >
              View PDF
            </a>
          );
        }
        return (
          <button
            type="button"
            onClick={() =>
              setPreviewVideo({
                show: true,
                url: fullUrl,
                title: row?.title || row?.name || "Video Preview",
              })
            }
            className="finance_link_text finance_link_button truncate_cell_text"
          >
            View Video
          </button>
        );
      },
      minWidth: "140px",
      grow: 1,
    },
    {
      name: "Created At",
      key: "created_at",
      selector: (row) => (row?.created_at ? formatDate(row.created_at) : "-"),
      minWidth: "150px",
      grow: 1,
    },
    {
      name: "Action",
      key: "action",
      selector: (row) => (
        <div className="table_action_btn">
          <button
            type="button"
            onClick={() => handleOpenEdit(row)}
            className="edit_btn"
            title="Edit"
          >
            <LuPencil />
          </button>
          <button
            type="button"
            onClick={() => handleDeleteClick(row)}
            className="delete_btn"
            title="Delete"
          >
            <RiDeleteBinLine />
          </button>
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
      grow: 0,
    },
  ];

  return (
    <div className="trip_cost_estimation_page pricing_templates_page">
      <Breadcrumbs values={breadcrumbs || ["Training Library", title]} />

      <ConfirmDeleteModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingItem(null);
        }}
        onConfirm={handleDeleteConfirm}
        loading={deleting}
        title="Are you sure you want to delete this content?"
        description={`Deleting "${
          deletingItem?.title || "this content"
        }" will permanently remove it.`}
      />

      {/* Video Preview Modal */}
      <CustomModal
        show={previewVideo.show}
        handleClose={() => setPreviewVideo({ show: false, url: "", title: "" })}
        heading={previewVideo.title || "Video Preview"}
        size="lg"
      >
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          {previewVideo.url && (
            <video
              src={previewVideo.url}
              controls
              autoPlay
              style={{
                maxWidth: "100%",
                maxHeight: "65vh",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                background: "#000",
              }}
            >
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      </CustomModal>

      <div className="access_view">
        <h2>{title}</h2>
        <div className="services_buttons">
          <button type="button" onClick={handleOpenCreate}>
            {plusIcon} Add Content
          </button>
        </div>
      </div>

      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={rows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={(nextLimit) => {
          setLimit(nextLimit);
          setCurrentPage(1);
        }}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearch={handleSearch}
        searchValue={searchText}
        loading={loading}
        paginationLoading={loading}
        limitOptions={[8, 10, 20, 50]}
        tableClassName="parts_table_fix"
      />

      {!loading && totalItems === 0 && (
        <div className="add_trip_suggestion_div" style={{ marginTop: 0 }}>
          <h4>There is nothing here...</h4>
          <p>
            There is nothing to view right now, please add content to get started.
          </p>
          <button
            type="button"
            className="main_btn"
            style={{ width: "auto", padding: "8px 20px" }}
            onClick={handleOpenCreate}
          >
            {plusIcon} Add Content
          </button>
        </div>
      )}

      {/* Create / Update Modal using exact project components with Formik + Yup */}
      <CustomModal
        show={showFormModal}
        handleClose={handleCloseFormModal}
        size="lg"
        heading={editingItem ? "Edit Content" : "Add Content"}
      >
        <Formik
          enableReinitialize
          initialValues={initialFormValues}
          validationSchema={contentValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
            handleSubmit,
            submitCount,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              <div className="add_user_modal">
                <h5>Content Information</h5>

                <div className="mb-3">
                  <Input
                    label="Title"
                    placeholder="Enter title"
                    type="text"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.title}
                    touched={Boolean(touched.title || submitCount > 0)}
                    required={true}
                  />
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Select
                      label="Category"
                      placeholder="Select category"
                      options={CATEGORY_OPTIONS}
                      name="category"
                      value={
                        CATEGORY_OPTIONS.find(
                          (opt) => opt.value === (values.category || category)
                        ) || CATEGORY_OPTIONS[0]
                      }
                      onChange={(option) => {
                        setFieldValue("category", option?.value || category);
                        setFieldTouched("category", true, true);
                      }}
                      onBlur={() => setFieldTouched("category", true, true)}
                      error={errors.category}
                      touched={Boolean(touched.category || submitCount > 0)}
                      required={true}
                      isDisabled={true}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Select
                      label="Type"
                      placeholder="Select type"
                      options={TYPE_OPTIONS}
                      name="type"
                      value={
                        TYPE_OPTIONS.find((opt) => opt.value === values.type) ||
                        TYPE_OPTIONS[0]
                      }
                      onChange={(option) => {
                        const newType = option?.value || "video";
                        setFieldValue("type", newType);
                        setFieldTouched("type", true, true);
                        if (uploadedFiles.length > 0) {
                          const existingName =
                            uploadedFiles[0]?.originalName ||
                            uploadedFiles[0]?.fileName ||
                            uploadedFiles[0]?.name ||
                            "";
                          if (newType === "pdf" && !isPdfFile(existingName)) {
                            setUploadedFiles([]);
                            setFieldValue("fileUrl", "");
                          } else if (newType === "video" && !isVideoFile(existingName)) {
                            setUploadedFiles([]);
                            setFieldValue("fileUrl", "");
                          }
                        }
                      }}
                      onBlur={() => setFieldTouched("type", true, true)}
                      error={errors.type}
                      touched={Boolean(touched.type || submitCount > 0)}
                      required={true}
                    />
                  </div>
                </div>

                <div className="photo_document_section mt-2">
                  <label
                    style={{
                      fontSize: "13px",
                      color: "#3D3F5C",
                      fontWeight: 400,
                      marginBottom: "6px",
                      display: "block",
                    }}
                  >
                    {values.type === "pdf" ? "Upload PDF (.pdf)" : "Upload Video (.mp4, .mov, etc.)"}{" "}
                    <span style={{ color: "red", fontSize: "13px" }}>*</span>
                  </label>
                  <FileUpload
                    destination="training"
                    files={uploadedFiles}
                    setFiles={(incoming) => {
                      setUploadedFiles((prev) => {
                        const next = typeof incoming === "function" ? incoming(prev) : incoming;
                        const safeList = Array.isArray(next) ? next : [];

                        if (safeList.length > 0) {
                          const newest = safeList[safeList.length - 1];
                          const name = String(
                            newest?.originalName ||
                              newest?.fileName ||
                              newest?.name ||
                              newest?.file_name ||
                              ""
                          ).trim();

                          if (name) {
                            if (values.type === "pdf" && !isPdfFile(name)) {
                              errorNotify("Only PDF (.pdf) files are allowed for PDF type");
                              return prev;
                            }
                            if (values.type === "video" && !isVideoFile(name)) {
                              errorNotify("Only video files (.mp4, .mov, etc.) are allowed for Video type");
                              return prev;
                            }
                          }
                        }

                        const path = safeList.length > 0 ? resolveUploadedPath(safeList[0]) : "";
                        if (typeof setFieldValue === "function") {
                          setFieldValue("fileUrl", path);
                          setFieldTouched?.("fileUrl", true, true);
                        }

                        return safeList;
                      });
                    }}
                    accept={
                      values.type === "pdf"
                        ? "application/pdf,.pdf"
                        : "video/*,.mp4,.mov,.avi,.mkv,.webm"
                    }
                    maxFiles={1}
                    maxFileSize={100}
                  />
                  {(touched.fileUrl || submitCount > 0) && errors.fileUrl && (
                    <div
                      className="error-message"
                      style={{
                        color: "#ff0000de",
                        fontSize: "12px",
                        marginTop: "3px",
                        marginLeft: "5px",
                      }}
                    >
                      {errors.fileUrl}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: "40px" }}>
                  <Button
                    cancelBtn={handleCloseFormModal}
                    submitBtn={handleSubmit}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    submitText={editingItem ? "Update" : "Save"}
                    type="submit"
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </CustomModal>
    </div>
  );
};

export default TrainingContentList;
